import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, Clock, MapPin, ChevronLeft, ChevronRight, RefreshCw, Link2, Unlink, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCollection } from "@/hooks/useCollection";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const TYPES = [
  { id: "class", label: "Class", color: "#8FA9E3" },
  { id: "meeting", label: "Meeting", color: "#6FC2B0" },
  { id: "deadline", label: "Deadline", color: "#EE9C86" },
  { id: "event", label: "Event", color: "#B69CE0" },
  { id: "personal", label: "Personal", color: "#78B7E0" },
];

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay();
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
const fmt = (d) => d.toISOString().slice(0, 10);

export default function CalendarPage() {
  const { items, loading, create, update, remove, load } = useCollection("events");
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [gConnected, setGConnected] = useState(false);
  const [gEmail, setGEmail] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [params, setParams] = useSearchParams();

  const loadGoogleStatus = async () => {
    try {
      const { data } = await api.get("/calendar/google/status");
      setGConnected(data.connected);
      setGEmail(data.email || "");
    } catch { /* */ }
  };

  useEffect(() => { loadGoogleStatus(); }, []);

  useEffect(() => {
    const g = params.get("google");
    if (!g) return;
    if (g === "connected") {
      toast.success("Google Calendar connected! Syncing your events…");
      refreshUser();
      loadGoogleStatus();
      runSync();
    } else if (g === "error") {
      toast.error("Couldn't connect Google Calendar. Please try again.");
    }
    setParams({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGoogle = async () => {
    if (!user?.is_pro) { navigate("/pricing"); return; }
    setConnecting(true);
    try {
      const { data } = await api.get("/calendar/google/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Google connection");
      setConnecting(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/calendar/google/sync");
      await load();
      toast.success(`Synced ${data.total} event${data.total === 1 ? "" : "s"} from Google`);
      setGConnected(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const disconnectGoogle = async () => {
    await api.post("/calendar/google/disconnect");
    setGConnected(false);
    setGEmail("");
    refreshUser();
    toast.success("Google Calendar disconnected");
  };

  function blank(date) {
    return { title: "", date: date || fmt(new Date()), start_time: "", end_time: "", type: "class", color: "#8FA9E3", location: "", notes: "" };
  }

  const openNew = (date) => { setEditing(null); setForm(blank(date)); setDialogOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm({ ...ev }); setDialogOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    const color = TYPES.find((t) => t.id === form.type)?.color || form.color;
    const body = { ...form, color };
    if (editing) { await update(editing.id, body); toast.success("Event updated"); }
    else { await create(body); toast.success("Event added"); }
    setDialogOpen(false);
  };

  const weekStart = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });
  const dayList = view === "week" ? days : [anchor];

  const move = (dir) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + dir * (view === "week" ? 7 : 1));
    setAnchor(d);
  };

  const eventsFor = (d) => items
    .filter((e) => e.date === fmt(d))
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

  return (
    <div>
      <PageHeader
        title="Calendar" icon={CalendarDays}
        subtitle="Your daily and weekly agenda, all in one calm view."
        action={
          <Button onClick={() => openNew()} data-testid="add-event-button" className="rounded-full bg-primary hover:bg-primary/90">
            <Plus size={18} className="mr-1" /> Add event
          </Button>
        }
      />

      <div className="tcc-card p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="google-sync-bar">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center">
            <CalendarDays size={20} className={gConnected ? "text-primary" : "text-slate-400"} />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-800 text-sm">Google Calendar</p>
            <p className="text-xs text-slate-400" data-testid="google-status-text">
              {gConnected ? `Connected · ${gEmail}` : "Sync your real schedule two ways"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {gConnected ? (
            <>
              <Button onClick={runSync} disabled={syncing} variant="outline" className="rounded-full" data-testid="google-sync-button">
                {syncing ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <RefreshCw size={16} className="mr-1.5" />} Sync now
              </Button>
              <Button onClick={disconnectGoogle} variant="ghost" className="rounded-full text-slate-500" data-testid="google-disconnect-button">
                <Unlink size={16} className="mr-1.5" /> Disconnect
              </Button>
            </>
          ) : (
            <Button onClick={connectGoogle} disabled={connecting} className="rounded-full bg-primary hover:bg-primary/90" data-testid="google-connect-button">
              {connecting ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Link2 size={16} className="mr-1.5" />}
              {user?.is_pro ? "Connect Google Calendar" : "Connect (Pro)"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => move(-1)} className="rounded-full" data-testid="cal-prev"><ChevronLeft size={18} /></Button>
          <span className="font-heading font-bold text-slate-800 min-w-[180px] text-center">
            {view === "week"
              ? `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
              : anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </span>
          <Button variant="outline" size="icon" onClick={() => move(1)} className="rounded-full" data-testid="cal-next"><ChevronRight size={18} /></Button>
          <Button variant="ghost" onClick={() => setAnchor(new Date())} className="rounded-full text-primary text-sm" data-testid="cal-today">Today</Button>
        </div>
        <div className="flex bg-slate-100 rounded-full p-1">
          {["day", "week"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${view === v ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : (
        <div className={`grid gap-4 ${view === "week" ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-7" : "grid-cols-1"}`}>
          {dayList.map((d) => {
            const isToday = fmt(d) === fmt(new Date());
            return (
              <div key={fmt(d)} className={`tcc-card p-4 ${isToday ? "ring-2 ring-primary/40" : ""}`} data-testid="calendar-day">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
                    <p className={`font-heading font-extrabold text-lg ${isToday ? "text-primary" : "text-slate-800"}`}>{d.getDate()}</p>
                  </div>
                  <button onClick={() => openNew(fmt(d))} className="h-7 w-7 rounded-lg bg-slate-100 hover:bg-primary/15 flex items-center justify-center text-slate-500" data-testid="day-add-event"><Plus size={15} /></button>
                </div>
                <div className="space-y-2">
                  {eventsFor(d).length === 0 && <p className="text-xs text-slate-300 py-2">—</p>}
                  {eventsFor(d).map((e) => (
                    <button key={e.id} onClick={() => openEdit(e)} data-testid="calendar-event"
                      className="w-full text-left p-2.5 rounded-xl group relative" style={{ background: `${e.color}22`, borderLeft: `3px solid ${e.color}` }}>
                      <p className="text-sm font-semibold text-slate-800 truncate pr-5">{e.title}</p>
                      {e.start_time && <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Clock size={11} />{e.start_time}{e.end_time && `–${e.end_time}`}</p>}
                      {e.location && <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={11} />{e.location}</p>}
                      <span onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive"><Trash2 size={13} /></span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-3xl" data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" className="rounded-xl mt-1" placeholder="Period 3 — Algebra" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>End</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl mt-1" placeholder="Room 204" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" rows={2} /></div>
          </div>
          <DialogFooter className="gap-2">
            {editing && <Button variant="outline" onClick={() => { remove(editing.id); setDialogOpen(false); }} className="rounded-full text-destructive border-destructive/30" data-testid="event-delete-button">Delete</Button>}
            <Button onClick={save} data-testid="event-save-button" className="rounded-full bg-primary hover:bg-primary/90">{editing ? "Save" : "Add event"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
